/**
 * The dashboard's Monitor lists (PRD-0075 R-D2): what is new (the top of the alert inbox), the
 * watched objects with when the case last saw them, and the standing sources with their last
 * intake. Each shows its first rows and links to the Monitor, where the lists are in full.
 */
import { BellRing, Eye, Inbox } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { AlertInboxRow } from '@/components/monitor/AlertInbox'
import { AlertObjectLabel } from '@/components/monitor/AlertObject'
import { SeverityBadge } from '@/components/monitor/SeverityBadge'
import type { StandingSourceRow } from '@/components/monitor/SourcesPane'
import { Badge } from '@/components/ui/badge'
import { formatDate } from '@/utils/date'
import { DashboardPanel, panelRows } from './DashboardPanel'
import type { DashboardPanelsView, DashboardWatchedView } from './dashboard-view'

const COPY = 'caseDashboard.panels'
const MONITOR = 'cases.monitor'
const ROW = 'flex flex-col gap-1 rounded-md border border-border/60 px-3 py-2'

function DayOf({ iso, title }: { iso: string; title: string }) {
  const { i18n } = useTranslation()
  return (
    <time dateTime={iso} title={title} className="shrink-0 tabular-nums">
      {formatDate(iso, i18n.language)}
    </time>
  )
}

function AlertRow({ row }: { row: AlertInboxRow }) {
  const { t, i18n } = useTranslation()
  const learned = t(`${MONITOR}.inbox.learnedOn`, {
    date: formatDate(row.learnedAt, i18n.language),
  })
  return (
    <li className={ROW}>
      <div className="flex min-w-0 items-center gap-2">
        <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
          <AlertObjectLabel row={row} />
        </span>
        {row.severity ? <SeverityBadge severity={row.severity} /> : null}
        {row.isNew ? <Badge variant="info">{t(`${MONITOR}.inbox.new`)}</Badge> : null}
      </div>
      <div className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
        <span className="min-w-0 truncate">
          {t(`${MONITOR}.inbox.rules.${row.ruleKind ?? 'unknown'}`)}
          {row.documentName ? ` · ${row.documentName}` : ''}
        </span>
        <span className="flex-1" />
        <DayOf iso={row.learnedAt} title={learned} />
      </div>
    </li>
  )
}

function WatchedRow({ item }: { item: DashboardWatchedView }) {
  const { t, i18n } = useTranslation()
  const seen = item.lastSeenAt
  return (
    <li className={ROW}>
      <div className="flex min-w-0 items-center gap-2">
        <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
          {item.label ?? t(`${MONITOR}.inbox.unknownSubject`)}
        </span>
        {item.entityType ? <Badge variant="outline">{item.entityType}</Badge> : null}
      </div>
      <span className="text-xs text-muted-foreground">
        {seen
          ? t(`${COPY}.watched.lastSeen`, { date: formatDate(seen, i18n.language) })
          : t(`${COPY}.watched.notSeen`)}
      </span>
    </li>
  )
}

function SourceRow({ row }: { row: StandingSourceRow }) {
  const { t, i18n } = useTranslation()
  const received = formatDate(row.lastReceivedAt, i18n.language)
  return (
    <li className={ROW}>
      <span className="flex min-w-0 items-center gap-1.5 text-sm font-medium text-foreground">
        <span className="shrink-0">
          {t(`${MONITOR}.sources.channels.${row.channel ?? 'unknown'}`)}
        </span>
        <span aria-hidden className="text-muted-foreground">
          ·
        </span>
        <span className="truncate" title={row.folder || undefined}>
          {row.folder || t(`${MONITOR}.sources.root`)}
        </span>
      </span>
      <div className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
        <span className="flex-1">
          {t(`${MONITOR}.sources.documents`, { count: row.documents })}
        </span>
        <DayOf
          iso={row.lastReceivedAt}
          title={t(`${COPY}.sources.lastIntake`, { date: received })}
        />
      </div>
    </li>
  )
}

export function MonitorPanels({ panels }: { panels: DashboardPanelsView }) {
  const { t } = useTranslation()
  const monitor = { href: panels.links.monitor, label: t(`${COPY}.openMonitor`) }
  const alerts = panelRows(panels.whatsNew, t(`${COPY}.whatsNew.empty`))
  const watched = panelRows(panels.watched, t(`${COPY}.watched.empty`))
  const sources = panelRows(panels.sources, t(`${COPY}.sources.empty`))
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
      <DashboardPanel
        heading={{ id: 'dashboard-whats-new', icon: BellRing, title: t(`${COPY}.whatsNew.title`) }}
        link={monitor}
        rows={alerts.rows}
      >
        <ul className="flex flex-col gap-2">
          {alerts.shown.map(row => (
            <AlertRow key={row.id} row={row} />
          ))}
        </ul>
      </DashboardPanel>
      <DashboardPanel
        heading={{ id: 'dashboard-watched', icon: Eye, title: t(`${COPY}.watched.title`) }}
        link={monitor}
        rows={watched.rows}
      >
        <ul className="flex flex-col gap-2">
          {watched.shown.map(item => (
            <WatchedRow key={item.uid} item={item} />
          ))}
        </ul>
      </DashboardPanel>
      <DashboardPanel
        heading={{ id: 'dashboard-sources', icon: Inbox, title: t(`${COPY}.sources.title`) }}
        link={monitor}
        rows={sources.rows}
      >
        <ul className="flex flex-col gap-2">
          {sources.shown.map(row => (
            <SourceRow key={row.key} row={row} />
          ))}
        </ul>
      </DashboardPanel>
    </div>
  )
}
